import type { SchedulerAppointment, SchedulerAppointmentSlot, TypeDate, TypeTimeOfDay } from '@/types/api'

export type ViewingAppointmentTimeSlotRow = {
  date?: string
  from?: string
  to?: string
}

export function parseTime(timeStr: string | null | undefined): TypeTimeOfDay | null {
  if (!timeStr || !timeStr.includes(':')) {
    return null
  }
  const parts = timeStr.split(':')
  return {
    hours: parseInt(parts[0], 10),
    minutes: parseInt(parts[1], 10),
    seconds: 0,
    nanos: 0
  }
}

export function timeToMinutes(timeStr: string | null | undefined): number | null {
  const time = parseTime(timeStr)
  if (!time) {
    return null
  }
  return time.hours * 60 + time.minutes
}

export function padTimePart(value: number | null | undefined): string {
  return ('0' + (value || 0)).slice(-2)
}

export function buildQuarterHourTimeList(): string[] {
  const list: string[] = []
  for (let i = 0; i <= 1425; i += 15) {
    let hours: string | number = Math.floor(i / 60)
    let minutes: string | number = i % 60
    if (minutes < 10) {
      minutes = '0' + minutes
    }
    if (hours < 10) {
      hours = '0' + hours
    }
    if (hours === 24) {
      hours = '00'
    }
    list.push(hours + ':' + minutes)
  }
  return list
}

export function getRowStartDate(row: ViewingAppointmentTimeSlotRow): Date | null {
  const time = parseTime(row.from)
  if (!row.date || !time) {
    return null
  }
  const date = new Date(row.date)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  date.setHours(time.hours, time.minutes, 0, 0)
  return date
}

export function rowsOverlap(a: ViewingAppointmentTimeSlotRow, b: ViewingAppointmentTimeSlotRow): boolean {
  if (!a.date || !b.date || a.date !== b.date) {
    return false
  }
  const aFrom = timeToMinutes(a.from)
  const aTo = timeToMinutes(a.to)
  const bFrom = timeToMinutes(b.from)
  const bTo = timeToMinutes(b.to)
  if (aFrom === null || aTo === null || bFrom === null || bTo === null) {
    return false
  }
  return aFrom < bTo && bFrom < aTo
}

export function isRowStartInPast(row: ViewingAppointmentTimeSlotRow, now: Date = new Date()): boolean {
  const start = getRowStartDate(row)
  return !!(start && start.getTime() < now.getTime())
}

function slotSortTimestamp(date?: TypeDate, time?: TypeTimeOfDay): number {
  if (!date) {
    return 0
  }
  const year = date.year || 0
  const month = date.month || 0
  const day = date.day || 0
  const hours = time?.hours || 0
  const minutes = time?.minutes || 0
  return year * 10000000000 + month * 100000000 + day * 1000000 + hours * 10000 + minutes * 100
}

export function compareSchedulerAppointmentSlots(
  a: Pick<SchedulerAppointmentSlot | SchedulerAppointment, 'date' | 'startTime'>,
  b: Pick<SchedulerAppointmentSlot | SchedulerAppointment, 'date' | 'startTime'>
): number {
  return slotSortTimestamp(a.date, a.startTime) - slotSortTimestamp(b.date, b.startTime)
}

/** Appointments without a calendarUserId are led by the on-site contact person. */
export function isContactLedAppointment(appointment?: Pick<SchedulerAppointment, 'calendarUserId'> | null): boolean {
  return !!appointment && !appointment.calendarUserId
}

type ViewingSlotTimeFields = Pick<SchedulerAppointmentSlot, 'startTime' | 'endTime'> & {
  ledByTenant?: boolean
}

/** Interested parties see start and end time for slots a tenant leads and only the start time for all others. */
export function shouldShowViewingSlotEndTime(slot?: ViewingSlotTimeFields | null): boolean {
  return !!slot?.ledByTenant
}

function formatTimeOfDay(time?: TypeTimeOfDay | null): string {
  if (!time) {
    return ''
  }
  return padTimePart(time.hours || 0) + ':' + padTimePart(time.minutes || 0)
}

export function formatSchedulerSlotTimeRange(
  slot?: ViewingSlotTimeFields | null,
  showEndTime: boolean = shouldShowViewingSlotEndTime(slot)
): string {
  if (!slot) {
    return ''
  }
  const from = formatTimeOfDay(slot.startTime)
  if (!from || !showEndTime) {
    return from
  }
  const to = formatTimeOfDay(slot.endTime)
  if (!to) {
    return from
  }
  return from + '–' + to
}
